import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { fmtDMYFromYMD, toISODate } from "../../../../../shared/utils/dateFormat";
import { formatMoney } from "../../../../../shared/utils/formatMoney";
import {
  PURCHASE_STATUS,
  PURCHASE_STATUS_LABELS,
} from "../utils/purchaseModel";

const columns = [
  { key: "date", label: "Fecha", width: "minmax(96px, .65fr)", align: "left" },
  { key: "supplier", label: "Proveedor", width: "minmax(210px, 2fr)", align: "left" },
  { key: "items", label: "Ítems", width: "minmax(68px, .5fr)", align: "center" },
  { key: "total", label: "Total", width: "minmax(125px, .9fr)", align: "left" },
  { key: "status", label: "Estado", width: "minmax(108px, .75fr)", align: "center" },
  { key: "actions", label: "Acciones", width: "minmax(150px, 1fr)", align: "center" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children, align = "left" }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 14,
        fontWeight: 650,
        textAlign: align,
      }}
    >
      {children}
    </Typography>
  );
}

function StatusPill({ status }) {
  const approved = status === PURCHASE_STATUS.APPROVED;
  return (
    <Box
      sx={{
        mx: "auto",
        width: 92,
        px: 1,
        py: 0.6,
        borderRadius: "20px",
        bgcolor: approved
          ? "rgba(33, 150, 83, 0.16)"
          : "rgba(233, 165, 35, 0.18)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: approved ? "rgba(18, 111, 63, 1)" : "rgba(142, 91, 0, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {PURCHASE_STATUS_LABELS[status] || status}
      </Typography>
    </Box>
  );
}

function ActionButton({ title, label, disabled, loading, color, onClick, children }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          type="button"
          size="small"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          sx={{
            width: 31,
            height: 31,
            bgcolor: "neutral.100",
            boxShadow: "0px 2px 6px rgba(23, 31, 76, 0.12)",
            "&:hover": { bgcolor: "secondary.100" },
          }}
        >
          {loading ? <CircularProgress size={16} /> : children(color)}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function formatPurchaseDate(value) {
  const isoDate = toISODate(value);
  return isoDate ? fmtDMYFromYMD(isoDate) : "—";
}

export default function PurchasesTable({
  rows = [],
  loading = false,
  activeOperation,
  onOpen,
  onPdf,
  onApprove,
  onDelete,
  emptyMessage = "Todavía no hay compras cargadas.",
}) {
  const operationInProgress = Boolean(activeOperation);

  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          scrollbarGutter: "stable",
        }}
      >
        <Box
          sx={{
            minWidth: 790,
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              flexShrink: 0,
              px: 2,
              py: 1.35,
              bgcolor: "neutral.500",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "primary.900",
                  fontSize: 14,
                  fontWeight: 750,
                  textAlign: column.align,
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "neutral.50",
            }}
          >
            {loading ? (
              <Stack
                spacing={1.25}
                sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}
              >
                <CircularProgress size={30} />
                <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>
                  Cargando compras...
                </Typography>
              </Stack>
            ) : rows.length ? (
              rows.map((purchase) => {
                const pending = purchase.status === PURCHASE_STATUS.PENDING;
                const operationForRow = activeOperation?.purchaseId === purchase.id;
                const supplierName = purchase.supplier?.name || "Sin proveedor";
                return (
                  <Box
                    key={purchase.id}
                    sx={{
                      ...gridSx,
                      minHeight: 68,
                      px: 2,
                      py: 0.8,
                      borderBottom: "1px solid rgba(0,0,0,.1)",
                      "&:hover": { bgcolor: "rgba(0,0,0,.025)" },
                    }}
                  >
                    <TextCell>{formatPurchaseDate(purchase.createdAt)}</TextCell>
                    <TextCell>{supplierName}</TextCell>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          minWidth: 38,
                          px: 1,
                          py: 0.45,
                          bgcolor: "secondary.50",
                          borderRadius: "14px",
                          boxShadow: "inset 0px 0px 2px rgba(96,96,113,.25)",
                        }}
                      >
                        <Typography
                          sx={{ color: "primary.900", fontSize: 13, fontWeight: 800, textAlign: "center" }}
                        >
                          {purchase.lines.length}
                        </Typography>
                      </Box>
                    </Box>
                    <TextCell>{formatMoney(purchase.total)}</TextCell>
                    <StatusPill status={purchase.status} />
                    <Stack direction="row" spacing={0.65} sx={{ justifyContent: "center" }}>
                      <ActionButton
                        title="Abrir Lista de compra"
                        label={`Abrir Lista de compra de ${supplierName}`}
                        disabled={loading || operationInProgress}
                        onClick={() => onOpen?.(purchase)}
                      >
                        {(color) => (
                          <VisibilityOutlinedIcon sx={{ fontSize: 18, color: color || "primary.700" }} />
                        )}
                      </ActionButton>
                      <ActionButton
                        title="Descargar PDF"
                        label={`Descargar PDF de ${supplierName}`}
                        disabled={loading || operationInProgress}
                        loading={operationForRow && activeOperation?.type === "pdf"}
                        onClick={() => onPdf?.(purchase)}
                      >
                        {() => (
                          <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "error.main" }} />
                        )}
                      </ActionButton>
                      {pending ? (
                        <>
                          <ActionButton
                            title="Aprobar compra"
                            label={`Aprobar compra de ${supplierName}`}
                            disabled={loading || operationInProgress}
                            onClick={() => onApprove?.(purchase)}
                          >
                            {() => (
                              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.dark" }} />
                            )}
                          </ActionButton>
                          <ActionButton
                            title="Eliminar compra"
                            label={`Eliminar compra de ${supplierName}`}
                            disabled={loading || operationInProgress}
                            onClick={() => onDelete?.(purchase)}
                          >
                            {() => (
                              <DeleteOutlineIcon sx={{ fontSize: 18, color: "primary.900" }} />
                            )}
                          </ActionButton>
                        </>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", p: 3 }}>
                <Typography
                  sx={{ color: "primary.900", fontSize: 14, fontWeight: 700, textAlign: "center" }}
                >
                  {emptyMessage}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
